/*
 * Write side of the blogs API, used only by the admin panel.
 *
 * ---------------------------------------------------------------------------
 * BACKEND CONTRACT (Spring Boot post service)
 * ---------------------------------------------------------------------------
 *   POST   /api/blogs        body: BlogDetail (no blogId) → 201 + the saved post
 *   PUT    /api/blogs/{id}   body: BlogDetail             → 200 + the saved post
 *   DELETE /api/blogs/{id}                                → 204
 *
 * Return the full saved resource from POST and PUT. A 204 forces the client into
 * an immediate GET to learn the server-assigned id, slug and updatedOn — one
 * wasted round trip on every save.
 *
 * All three require a Bearer token; the backend is the real authority on
 * authorisation. Reads are public and live in src/api/blogs.js.
 */

import { API_ENDPOINTS, apiCall } from '../api';
import { normaliseBlog } from './blogs';

async function readOrThrow(res, action) {
  if (!res.ok) {
    // Surface the backend's message when it sends one — "Failed to save (400)"
    // tells the author nothing they can act on.
    let detail = '';
    try {
      const body = await res.json();
      detail = body.message || body.error || '';
    } catch (_) {
      /* not JSON */
    }
    throw new Error(detail || `Could not ${action} (HTTP ${res.status}).`);
  }
  return res.json();
}

/**
 * Create or update, depending on whether `post.blogId` is set.
 * @returns {Promise<object>} the saved post as the server/store recorded it
 */
export async function saveBlog(post) {
  const isUpdate = Boolean(post.blogId);

  // On create, omit blogId entirely — the backend assigns it.
  const { blogId, ...body } = post;
  const url = isUpdate
    ? `${API_ENDPOINTS.BLOGS}/${encodeURIComponent(blogId)}`
    : API_ENDPOINTS.BLOGS;

  const res = await apiCall(url, {
    method: isUpdate ? 'PUT' : 'POST',
    body: JSON.stringify(isUpdate ? post : body),
  });

  return normaliseBlog(await readOrThrow(res, isUpdate ? 'update the post' : 'publish the post'));
}

export async function deleteBlog(blogId) {
  const res = await apiCall(`${API_ENDPOINTS.BLOGS}/${encodeURIComponent(blogId)}`, {
    method: 'DELETE',
  });

  // 204 is the expected success; there is no body to read.
  if (!res.ok) await readOrThrow(res, 'delete the post');
}

